// Shared frame rendered by every Vike `+Page.tsx`.
//
// Responsibilities (replaces the old root `pages/+Page.tsx` + React Router):
//  - Seed the react-query cache from server-fetched data (`+data.ts`) so hooks
//    resolve synchronously without refetching.
//  - Mount the app `Providers` + `App` shell (theme gate, layout, dev tooling).
//  - Initialise the CMS styler listener.
//
// Runs on both server and client (ssr: true). Cache seeding is split: the
// react-query writes are SSR-safe and run everywhere (they produce the SSR
// HTML), while ORM store mutations are client-only (see seedCacheFromSSR).

import "@superbright/react-component-library/style.css";
import { useEffect, useRef, type ReactNode } from "react";
import { useData } from "vike-react/useData";

import type { Data } from "../../pages/_types";
import { App, Providers } from "../app";
import { initCmsStylerListener } from "../cmsStyler";
import { seedCacheFromSSR } from "../ssr/seedCache";
import { applyThemeTokens } from "../theme/themeConfig";

let cmsStylerInitialised = false;

export function PageFrame({ children }: { children: ReactNode }) {
  const data = useData<Data | undefined>();
  const seededRef = useRef(false);

  if (!seededRef.current) {
    seededRef.current = true;
    seedCacheFromSSR(data);
  }

  useEffect(() => {
    // Theme tokens are baked into <head> during SSR (+Head.tsx). Re-apply on
    // the client to cover SPA navigations where the slug/theme changes.
    if (data?.theme_tokens) {
      applyThemeTokens(data.theme_tokens);
    }
  }, [data?.theme_tokens]);

  useEffect(() => {
    if (cmsStylerInitialised) return;
    cmsStylerInitialised = true;
    initCmsStylerListener();
  }, []);

  return (
    <Providers>
      <App ssrHasCustomFonts={!!data?.font_face_css}>{children}</App>
    </Providers>
  );
}
